import { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, IconButton, List, ListItem, ListItemText, ListItemIcon, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';

/**
 * DropFiles - A customizable drag and drop file upload component
 * 
 * @param {Object} props
 * @param {boolean} props.multiple - Allow multiple file uploads
 * @param {function} props.onChange - Callback when files change (files, event)
 * @param {function} props.onDrop - Callback when files are dropped
 * @param {Array} props.acceptedFileTypes - Array of accepted file types (e.g. ['.pdf', '.jpg'])
 * @param {number} props.maxFileSize - Maximum file size in bytes
 * @param {number} props.maxFiles - Maximum number of files allowed when multiple is true
 * @param {Object} props.sx - Custom styling for the container
 * @param {boolean} props.showFileList - Show the list of uploaded files
 * @param {string} props.dropzoneText - Custom text for the dropzone
 * @param {string} props.buttonText - Custom text for the upload button
 * @param {boolean} props.base64 - Whether to convert files to base64
 * @returns {JSX.Element}
 */
export default function DropFiles({
    multiple = false,
    onChange,
    onDrop,
    acceptedFileTypes = [],
    maxFileSize,
    maxFiles = 10,
    sx = {},
    showFileList = true,
    dropzoneText = "Arrastra y suelta archivos aquí",
    buttonText = "Seleccionar archivos",
    base64 = false
}) {
    const [files, setFiles] = useState([]);
    const [isDragActive, setIsDragActive] = useState(false);
    const [errors, setErrors] = useState([]);
    const [uploadStatus, setUploadStatus] = useState('idle'); // idle, uploading, success, error
    
    const fileInputRef = useRef(null);
    const theme = useTheme();

    // Clear previous errors when files change
    useEffect(() => {
        setErrors([]);
    }, [files]);

    // Convert file to base64
    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (error) => reject(error);
        });
    };

    const handleFileChange = (event) => {
        const selectedFiles = Array.from(event.target.files);
        processFiles(selectedFiles, event);
    };

    const handleDragEnter = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(true);
    };

    const handleDragLeave = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
    };

    const handleDragOver = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();
        setIsDragActive(false);
        
        const droppedFiles = Array.from(event.dataTransfer.files);
        processFiles(droppedFiles, event);
        
        if (onDrop) {
            onDrop(droppedFiles, event);
        }
    };

    const processFiles = async (newFiles, event) => {
        const newErrors = [];
        
        // Check max files limit
        if (multiple && files.length + newFiles.length > maxFiles) {
            newErrors.push(`No puedes subir más de ${maxFiles} archivos.`);
            setErrors(newErrors);
            return;
        }
        
        // Validate each file
        const validFiles = newFiles.filter(file => {
            // Check file type
            if (acceptedFileTypes.length > 0) {
                const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
                if (!acceptedFileTypes.includes(fileExtension) && !acceptedFileTypes.includes(file.type)) {
                    newErrors.push(`Tipo de archivo no permitido: ${file.name}`);
                    return false;
                }
            }
            
            // Check file size
            if (maxFileSize && file.size > maxFileSize) {
                const sizeMB = (maxFileSize / (1024 * 1024)).toFixed(2);
                newErrors.push(`Archivo demasiado grande: ${file.name}. Máximo: ${sizeMB} MB`);
                return false;
            }
            
            return true;
        });
        
        setErrors(newErrors);
        
        if (validFiles.length > 0) {
            let updatedFiles;
            
            if (multiple) {
                updatedFiles = [...files, ...validFiles];
            } else {
                updatedFiles = validFiles.slice(0, 1);
            }

            if (base64) {
                const convertedFiles = await Promise.all(updatedFiles.map(async (file) => {
                    const base64String = await convertToBase64(file);
                    return {
                        name: file.name,
                        size: file.size,
                        type: file.type,
                        lastModified: file.lastModified,
                        file: file,
                        base64: base64String
                    };
                }));
                setFiles(convertedFiles);
                if (onChange) {
                    onChange(convertedFiles, event);
                }
            } else {
                setFiles(updatedFiles);
                if (onChange) {
                    onChange(updatedFiles, event);
                }
            }
        }
    };

    const removeFile = (index) => {
        const updatedFiles = [...files];
        updatedFiles.splice(index, 1);
        setFiles(updatedFiles);
    };

    const clearFiles = () => {
        setFiles([]);
    };

    const getFileSize = (bytes) => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    // Format file list to be accessible outside the component
    const getFormattedFiles = async () => {
        if (base64) {
            const convertedFiles = await Promise.all(files.map(async (file) => {
                const base64String = await convertToBase64(file);
                return {
                    name: file.name,
                    size: file.size,
                    type: file.type,
                    file: file,
                    base64: base64String
                };
            }));
            return convertedFiles;
        }
        return files.map(file => ({
            name: file.name,
            size: file.size,
            type: file.type,
            file: file
        }));
    };

    // Notify parent component of file changes
    useEffect(() => {
        if (onChange && !base64) {
            onChange(files, null);
        }
    }, [files, onChange, base64]);

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    width: '100%',
                    border: `2px dashed ${isDragActive ? theme.palette.primary.main : theme.palette.divider}`,
                    borderRadius: '8px',
                    padding: 3,
                    textAlign: 'center',
                    backgroundColor: isDragActive 
                        ? theme.palette.mode === 'dark' 
                            ? 'rgba(120, 120, 255, 0.1)' 
                            : 'rgba(25, 118, 210, 0.05)'
                        : 'transparent',
                    transition: 'all 0.2s ease',
                    cursor: 'pointer',
                    position: 'relative',
                    minHeight: '150px',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    ...sx
                }}
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current.click()}
            >
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                    multiple={multiple}
                    accept={acceptedFileTypes.join(',')}
                />
                
                <CloudUploadIcon sx={{ fontSize: 40, color: theme.palette.primary.main, mb: 2 }} />
                
                <Typography variant="h6" gutterBottom>
                    {dropzoneText}
                </Typography>
                
                <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                    {multiple ? `Puedes subir hasta ${maxFiles} archivos` : 'Solo un archivo permitido'}
                </Typography>
                
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={(e) => {
                        e.stopPropagation();
                        fileInputRef.current.click();
                    }}
                >
                    {buttonText}
                </Button>
            </Box>
            
            {/* Error messages */}
            {errors.length > 0 && (
                <Box sx={{ mt: 2, color: theme.palette.error.main }}>
                    {errors.map((error, index) => (
                        <Typography key={index} variant="body2" color="error" sx={{ display: 'flex', alignItems: 'center' }}>
                            <WarningIcon sx={{ mr: 1, fontSize: 16 }} />
                            {error}
                        </Typography>
                    ))}
                </Box>
            )}
            
            {/* File List */}
            {showFileList && files.length > 0 && (
                <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                        <Typography variant="subtitle2">
                            {files.length} {files.length === 1 ? 'archivo seleccionado' : 'archivos seleccionados'}
                        </Typography>
                        <Button 
                            size="small" 
                            color="error" 
                            onClick={(e) => {
                                e.stopPropagation();
                                clearFiles();
                            }}
                        >
                            Borrar todos
                        </Button>
                    </Box>
                    
                    <List sx={{ 
                        maxHeight: '200px', 
                        overflow: 'auto',
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '4px',
                    }}>
                        {files.map((file, index) => (
                            <ListItem
                                key={index}
                                secondaryAction={
                                    <IconButton 
                                        edge="end" 
                                        aria-label="delete" 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            removeFile(index);
                                        }}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                }
                                sx={{ 
                                    borderBottom: index < files.length - 1 ? `1px solid ${theme.palette.divider}` : 'none',
                                }}
                            >
                                <ListItemIcon>
                                    <InsertDriveFileIcon color="primary" />
                                </ListItemIcon>
                                <ListItemText
                                    primary={file.name}
                                    secondary={getFileSize(file.size)}
                                    primaryTypographyProps={{
                                        noWrap: true,
                                        style: { maxWidth: '200px' }
                                    }}
                                />
                                {uploadStatus === 'success' && (
                                    <Chip 
                                        icon={<CheckCircleIcon />} 
                                        label="Éxito" 
                                        size="small" 
                                        color="success" 
                                        sx={{ ml: 1 }} 
                                    />
                                )}
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}
        </Box>
    );
}
